import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Sparkles, X, Send, Loader2, RotateCcw, MessageCircle } from "lucide-react";
import api from "../lib/api";
import { streamAi, newSessionId } from "../lib/aiStream";
import { useAuth } from "../context/AuthContext";
import { useMT } from "../lib/miniI18n";

const QUICK = ["How do I recharge?", "How to withdraw winnings?", "How to play Singles & Doubles?", "When is the next result?"];

/* Floating "Royal Assistant" chat bubble — multi-turn, session persisted per user */
export function RoyalAssistant() {
  const { user } = useAuth();
  const mt = useMT();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [sid, setSid] = useState("");
  const endRef = useRef(null);
  const key = user ? `rl_ai_session_${user.id.slice(0, 8)}` : "";

  useEffect(() => {
    if (!user) return;
    const s = localStorage.getItem(key) || newSessionId(user.id);
    localStorage.setItem(key, s);
    setSid(s);
    api.get("/ai/chat/history", { params: { session_id: s } }).then((r) => setMsgs((r.data || []).map((d) => ({ role: d.role, content: d.content })))).catch(() => {});
  }, [user, key]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  if (!user || location.pathname.startsWith("/admin") || location.pathname.startsWith("/agent") || location.pathname.startsWith("/mini/")) return null;

  const send = async (q) => {
    const m = (q ?? text).trim();
    if (!m || busy) return;
    setText("");
    setMsgs((p) => [...p, { role: "user", content: m }, { role: "assistant", content: "", streaming: true }]);
    setBusy(true);
    try {
      await streamAi("/ai/chat", { session_id: sid, message: m }, (full) => setMsgs((p) => { const c = [...p]; c[c.length - 1] = { role: "assistant", content: full, streaming: true }; return c; }));
      setMsgs((p) => { const c = [...p]; c[c.length - 1] = { ...c[c.length - 1], streaming: false }; return c; });
    } catch (e) {
      setMsgs((p) => { const c = [...p]; c[c.length - 1] = { role: "assistant", content: `⚠️ ${e.message || "Assistant unavailable"}`, streaming: false, error: true }; return c; });
    } finally { setBusy(false); }
  };
  const reset = () => { const s = newSessionId(user.id); localStorage.setItem(key, s); setSid(s); setMsgs([]); };
  const handoff = async () => {
    try { const { data } = await api.get("/ai/handoff", { params: { session_id: sid } }); window.open(data.url, "_blank"); }
    catch { window.open("https://wa.me/", "_blank"); }
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} className="fixed right-3 bottom-[88px] z-[60] w-12 h-12 rounded-full rl-gold-bg shadow-[0_8px_24px_rgba(245,158,11,.45)] flex items-center justify-center active:scale-95 transition-transform rl-fade-up" data-testid="assistant-bubble" aria-label="Royal Assistant">
          <Sparkles className="w-6 h-6 text-slate-900" />
        </button>
      )}
      {open && (
        <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-[2px] flex items-end sm:items-center justify-center" onClick={() => setOpen(false)} data-testid="assistant-sheet">
          <div className="w-full sm:max-w-sm bg-white rounded-t-3xl sm:rounded-3xl flex flex-col h-[78vh] sm:h-[600px] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 flex items-center gap-2 text-white" style={{ background: "linear-gradient(120deg,#1e1b4b,#1e3a8a)" }}>
              <div className="w-9 h-9 rounded-full rl-gold-bg flex items-center justify-center"><Sparkles className="w-5 h-5 text-slate-900" /></div>
              <div className="flex-1"><div className="font-display font-bold text-sm">Royal Assistant</div><div className="text-[10px] text-blue-100">{mt("Ask about recharge, withdraw, rules, results")}</div></div>
              <button onClick={reset} className="p-1.5 text-blue-100" title="New chat" data-testid="assistant-reset"><RotateCcw className="w-4 h-4" /></button>
              <button onClick={() => setOpen(false)} className="p-1.5" data-testid="assistant-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-slate-50" data-testid="assistant-messages">
              {!msgs.length && (
                <div className="text-center pt-6" data-testid="assistant-empty">
                  <div className="text-sm font-semibold text-slate-700">{mt("Vanakkam")} {user.name?.split(" ")[0] || ""} 👑</div>
                  <div className="text-xs text-slate-500 mt-1 mb-4">{mt("I can help you in your language. Try:")}</div>
                  <div className="flex flex-wrap gap-1.5 justify-center">{QUICK.map((q) => <button key={q} onClick={() => send(mt(q))} className="text-[11px] bg-white border border-slate-200 rounded-full px-3 py-1.5 text-slate-700 active:scale-95" data-testid="assistant-quick">{mt(q)}</button>)}</div>
                </div>
              )}
              {msgs.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-blue-600 text-white rounded-br-md" : m.error ? "bg-red-50 text-red-700 border border-red-200" : "bg-white text-slate-800 border border-slate-200 rounded-bl-md"}`} data-testid={`assistant-msg-${m.role}`}>
                    {m.content}{m.streaming && <span className="inline-block w-1.5 h-4 bg-amber-400 ml-0.5 align-middle animate-pulse" />}
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>
            <div className="p-2 border-t border-slate-200 bg-white flex items-center gap-2">
              <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={mt("Type your question…")} className="flex-1 rl-card bg-slate-100 px-3 py-2.5 text-sm outline-none" data-testid="assistant-input" />
              <button onClick={() => send()} disabled={busy || !text.trim()} className="w-10 h-10 rounded-xl bg-blue-600 disabled:opacity-40 text-white flex items-center justify-center" data-testid="assistant-send">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</button>
            </div>
            <div className="bg-white pb-2 flex items-center justify-center gap-3">
              <button onClick={handoff} className="text-[11px] font-bold text-[#128C7E] flex items-center gap-1 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1" data-testid="assistant-handoff"><MessageCircle className="w-3.5 h-3.5" />{mt("Talk to human on WhatsApp")}</button>
              <button onClick={() => { setOpen(false); navigate("/me"); }} className="text-[10px] text-slate-400" data-testid="assistant-support-link">{mt("Me → Support")}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
